import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import { Element, scrollTo } from 'platform/utilities/scroll';
import { focusElement } from 'platform/utilities/ui';
import { SCROLL_ELEMENT_SUFFIX } from '../../../../utilities/constants';
import get from '../../../../utilities/data/get';
import SchemaForm from '../components/SchemaForm';
import { hrefWithBasename, showReviewField } from '../helpers';
import { getPreviousPagePath, checkValidPagePath } from '../routing';
import ArrayField from './ArrayField';
import { getPageKey } from '../utilities/review';
import { STATEMENT_OF_TRUTH_ID, STATEMENT_OF_TRUTH_SCROLL } from '../constants';
import {
  FLAT_REVIEW_CHANGED_MESSAGE,
  FLAT_REVIEW_RENDER_OPTIONS,
  FLAT_REVIEW_SUCCESS_BODY_AFTER,
  FLAT_REVIEW_SUCCESS_BODY_BEFORE,
  FLAT_REVIEW_SUCCESS_JUMP_TEXT,
  FLAT_REVIEW_SUCCESS_MESSAGE,
  flatReviewSectionId,
  slugifyHeadingId,
} from './flatReview';
import { getSchemaPageEditLocation } from './reviewEditTrip';
import {
  getReviewChapterTitle,
  getReviewPageTitle,
  getSchemaPageContent,
  shouldHideExpandedPageTitle,
} from './reviewPageContent';

const noop = () => {};

const FlatEditLink = ({ href, label, onEdit }) => (
  <va-link
    active
    href={href}
    text="Edit"
    label={label}
    onClick={event => {
      event.preventDefault();
      onEdit();
    }}
  />
);

FlatEditLink.propTypes = {
  href: PropTypes.string,
  label: PropTypes.string,
  onEdit: PropTypes.func,
};

const jumpToStatementOfTruth = event => {
  event.preventDefault();
  const target = document.getElementById(STATEMENT_OF_TRUTH_ID);
  scrollTo(STATEMENT_OF_TRUTH_SCROLL);
  focusElement(target?.querySelector('h2, h3') || target);
};

const FlatSuccessAlert = () => (
  <va-alert
    id="flat-review-success-alert"
    status="success"
    visible
    class="vads-u-margin-bottom--2"
    role="alert"
  >
    <h3 slot="headline">{FLAT_REVIEW_SUCCESS_MESSAGE}</h3>
    <p>
      {FLAT_REVIEW_SUCCESS_BODY_BEFORE}
      <a href={`#${STATEMENT_OF_TRUTH_ID}`} onClick={jumpToStatementOfTruth}>
        {FLAT_REVIEW_SUCCESS_JUMP_TEXT}
      </a>
      {FLAT_REVIEW_SUCCESS_BODY_AFTER}
    </p>
  </va-alert>
);

/**
 * Always-open chapter for the flat review page. Schema Edit opens the real
 * step with Update/Cancel. CustomPageReview keeps its existing edit control,
 * including the prefill Update/Cancel trip.
 */
function ReviewFlatChapter({
  chapterFormConfig,
  chapterKey,
  expandedPages,
  form,
  formContext,
  formOptions,
  location,
  onEdit,
  pageList,
  router,
  setData,
  uploadFile,
  urlPrefix,
  viewedPages,
  changedChapters = [],
  successAlert = '',
  successPageKey = '',
}) {
  const chapterTitle = getReviewChapterTitle(chapterFormConfig, form);
  const headingId = slugifyHeadingId(chapterTitle, chapterKey);
  const ChapterDescription = chapterFormConfig.reviewDescription;

  const goToPath = (customPath, options = {}) => {
    const { force, chapterKey: stateChapterKey } = options;
    const pages = pageList || expandedPages;
    const path =
      customPath && (force || checkValidPagePath(pages, form.data, customPath))
        ? customPath
        : getPreviousPagePath(pages, form.data, location?.pathname);

    const { pathname, search } = new URL(path, window.location.origin);
    router.push({
      pathname,
      search,
      state: { chapterKey: stateChapterKey },
    });
  };

  const renderSchemaPage = page => {
    const pageState = form.pages[page.pageKey];
    const fullPageKey = `${page.pageKey}${page.index ?? ''}`;

    if (!pageState) {
      return null;
    }

    const { pageSchema, pageUiSchema, pageData, arrayFields } =
      getSchemaPageContent(page, pageState, form.data);

    const hasError = form.formErrors?.errors?.some(
      err => fullPageKey === getPageKey(err),
    );

    const visibleFields =
      pageSchema &&
      Object.entries(pageSchema.properties).filter(([propName]) =>
        showReviewField(
          propName,
          pageSchema,
          pageUiSchema,
          form.data,
          formContext,
        ),
      ).length > 0;

    if (!visibleFields && arrayFields.length === 0) {
      return null;
    }

    const title = getReviewPageTitle(
      page.reviewTitle || page.title || '',
      pageData,
    );
    const showPageTitle =
      Boolean(title) &&
      !shouldHideExpandedPageTitle(expandedPages, chapterTitle, title);
    const classes = classNames(
      'form-review-panel-page',
      'schemaform-flat-review__section',
      {
        'schemaform-review-page-error':
          hasError || !viewedPages.has(fullPageKey),
      },
    );
    const editLocation = getSchemaPageEditLocation(page, pageList);
    const editHref = editLocation
      ? hrefWithBasename(
          router,
          `${editLocation.pathname}${editLocation.search}`,
        )
      : '';
    const reviewFormContext = {
      ...formContext,
      formOptions,
      renderOptions: FLAT_REVIEW_RENDER_OPTIONS,
      flatReview: true,
    };

    return (
      <div
        key={fullPageKey}
        className={classes}
        data-flat-review-page={fullPageKey}
      >
        <Element name={`${fullPageKey}${SCROLL_ELEMENT_SUFFIX}`} />
        <div className="schemaform-flat-review__section-header">
          {showPageTitle ? (
            <h3 id={flatReviewSectionId(fullPageKey)}>{title}</h3>
          ) : null}
          {editLocation ? (
            <FlatEditLink
              href={editHref}
              label={title ? `Edit ${title}` : 'Edit'}
              onEdit={() => router.push(editLocation)}
            />
          ) : null}
        </div>
        {successAlert && successPageKey === fullPageKey ? (
          <FlatSuccessAlert />
        ) : null}
        {pageSchema && (
          <SchemaForm
            name={page.pageKey}
            title={title}
            data={pageData}
            appStateData={page.appStateData}
            schema={pageSchema}
            uiSchema={pageUiSchema}
            trackingPrefix={form.trackingPrefix}
            hideHeaderRow
            hideTitle
            pagePerItemIndex={page.index}
            onBlur={noop}
            onEdit={noop}
            onSubmit={noop}
            onChange={formData => setData(formData)}
            uploadFile={uploadFile}
            reviewMode
            formContext={reviewFormContext}
          >
            <div />
          </SchemaForm>
        )}
        {arrayFields.map(arrayField => (
          <div key={arrayField.path} className="form-review-array">
            <ArrayField
              pageKey={page.pageKey}
              pageTitle={page.title}
              arrayData={get(arrayField.path, form.data)}
              formData={form.data}
              appStateData={page.appStateData}
              formContext={reviewFormContext}
              pageConfig={page}
              schema={arrayField.schema}
              uiSchema={arrayField.uiSchema}
              trackingPrefix={form.trackingPrefix}
              setData={setData}
              path={arrayField.path}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderCustomPage = page => {
    const pageState = form.pages[page.pageKey];
    const CustomPage = pageState?.CustomPage || page.CustomPage;
    const CustomPageReview =
      pageState?.CustomPageReview || page.CustomPageReview;
    const fullPageKey = getPageKey(page);
    const editing = pageState?.showPagePerItem
      ? pageState.editMode?.[page.index]
      : pageState?.editMode;
    const customPageFormContext = { ...formContext, flatReview: true };

    if (editing && CustomPage) {
      const pageTitle = getReviewPageTitle(page.title, form.data);
      return (
        <div
          key={fullPageKey}
          className="form-review-panel-page schemaform-flat-review__section"
        >
          <Element name={`${fullPageKey}${SCROLL_ELEMENT_SUFFIX}`} />
          <CustomPage
            name={page.pageKey}
            chapterKey={chapterKey}
            title={page.title}
            trackingPrefix={form.trackingPrefix}
            uploadFile={uploadFile}
            onReviewPage
            setFormData={setData}
            data={form.data}
            updatePage={() => onEdit(page.pageKey, false, page.index)}
            updatePageAriaLabel={
              pageTitle ? `Update ${pageTitle}` : 'Update page'
            }
            recalculateErrors={noop}
            pagePerItemIndex={page.index}
            goBack={noop}
            goForward={noop}
            goToPath={goToPath}
            urlPrefix={urlPrefix || ''}
            formContext={customPageFormContext}
          />
        </div>
      );
    }

    if (!CustomPageReview) {
      return renderSchemaPage(page);
    }

    const hasError = form.formErrors?.errors?.some(
      err => fullPageKey === getPageKey(err),
    );

    return (
      <div
        key={fullPageKey}
        className={classNames(
          'form-review-panel-page',
          'schemaform-flat-review__section',
          {
            'schemaform-review-page-error':
              hasError || !viewedPages.has(fullPageKey),
          },
        )}
        data-flat-review-page={fullPageKey}
      >
        <Element name={`${fullPageKey}${SCROLL_ELEMENT_SUFFIX}`} />
        {successAlert && successPageKey === fullPageKey ? (
          <FlatSuccessAlert />
        ) : null}
        <CustomPageReview
          key={`${page.pageKey}Review${page.index ?? ''}`}
          chapterKey={chapterKey}
          editPage={() => onEdit(page.pageKey, !editing, page.index)}
          name={page.pageKey}
          title={page.title}
          data={form.data}
          fullData={form.data}
          setFormData={setData}
          pagePerItemIndex={page.index}
          goToPath={goToPath}
          recalculateErrors={noop}
          urlPrefix={urlPrefix || ''}
          onReviewPage
          formContext={customPageFormContext}
        />
      </div>
    );
  };

  return (
    <section
      className="schemaform-flat-review__chapter"
      data-chapter={chapterKey}
    >
      <Element name={`chapter${chapterKey}${SCROLL_ELEMENT_SUFFIX}`} />
      <h2 id={headingId} className="schemaform-flat-review__chapter-title">
        {chapterTitle}
      </h2>
      {changedChapters.includes(chapterKey) ? (
        <va-alert
          slim
          status="error"
          visible
          class="vads-u-margin-bottom--2"
          role="alert"
        >
          {FLAT_REVIEW_CHANGED_MESSAGE}
        </va-alert>
      ) : null}
      {ChapterDescription && (
        <ChapterDescription
          viewedPages={viewedPages}
          pageKeys={expandedPages.map(page => page.pageKey)}
          formData={form.data}
        />
      )}
      {expandedPages?.map(page => {
        const pageConfig = form.pages[page.pageKey] || page;
        return pageConfig.CustomPageReview || page.CustomPageReview
          ? renderCustomPage(page)
          : renderSchemaPage(page);
      })}
    </section>
  );
}

ReviewFlatChapter.propTypes = {
  chapterFormConfig: PropTypes.object.isRequired,
  chapterKey: PropTypes.string.isRequired,
  expandedPages: PropTypes.array.isRequired,
  form: PropTypes.object.isRequired,
  setData: PropTypes.func.isRequired,
  viewedPages: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  changedChapters: PropTypes.arrayOf(PropTypes.string),
  formContext: PropTypes.object,
  formOptions: PropTypes.object,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  pageList: PropTypes.array,
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
  successAlert: PropTypes.string,
  successPageKey: PropTypes.string,
  uploadFile: PropTypes.func,
  urlPrefix: PropTypes.string,
};

export default withRouter(ReviewFlatChapter);
export { ReviewFlatChapter };
